import {useQuery} from '@tanstack/react-query'
import {Link} from 'react-router-dom'
import {CheckCircle, ShieldAlert} from 'lucide-react'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'

interface SeverityBadgeProps {
    entityTable: string
    entityId: string
}

export function SeverityBadge({entityTable, entityId}: SeverityBadgeProps) {
    const i18n = useI18n()
    const services = useServices()

    const {data, isLoading} = useQuery({
        queryKey: ['violation-summary', entityTable, entityId],
        queryFn: () => services.violations.summaryByEntity(entityTable, entityId),
    })

    if (isLoading || !data) return null

    const total = data.critical + data.error + data.warn + data.info
    const violationsHref = routes.schema.violations.list()

    if (total === 0) {
        return (
            <InlineRow gap="xs" align="center" data-testid="severity-badge-zero">
                <Icon icon={CheckCircle} size="sm" className="text-success"/>
                <MutedText size="sm">{i18n.schemaViolations.noOpenViolations}</MutedText>
            </InlineRow>
        )
    }

    return (
        <InlineRow gap="xs" align="center" data-testid="severity-badge-row">
            <Link
                to={violationsHref}
                aria-label={i18n.schemaViolations.viewViolations}
                data-testid="severity-badge-link"
            >
                <InlineRow gap="xs" align="center">
                    {data.critical > 0 && (
                        <Badge variant={BadgeVariant.DESTRUCTIVE} data-testid="severity-badge-critical">
                            {i18n.schemaViolations.severityCountCritical({count: data.critical})}
                        </Badge>
                    )}
                    {data.error > 0 && (
                        <Badge variant={BadgeVariant.DESTRUCTIVE} data-testid="severity-badge-error">
                            {i18n.schemaViolations.severityCountError({count: data.error})}
                        </Badge>
                    )}
                    {data.warn > 0 && (
                        <Badge variant={BadgeVariant.WARNING} data-testid="severity-badge-warn">
                            {i18n.schemaViolations.severityCountWarn({count: data.warn})}
                        </Badge>
                    )}
                </InlineRow>
            </Link>
            {data.hasReferenceBroken && (
                <Badge variant={BadgeVariant.OUTLINE} data-testid="severity-badge-reference-broken">
                    <Icon icon={ShieldAlert} size="xs" className="mr-1"/>
                    {i18n.schemaViolations.referenceBroken}
                </Badge>
            )}
        </InlineRow>
    )
}
